// konva/overlays/PathEditOverlay.ts — Interactive overlay for editing path points and handles
import Konva from 'konva';
import type { PathPoint } from '../../core/tools/pathUtils';
import { pathPointsToSVG } from '../../core/tools/pathUtils';

export interface PathEditCallbacks {
  onPointDragEnd: (index: number, x: number, y: number) => void;
  onPointDragMove: (index: number, x: number, y: number) => void;
  onHandleDragEnd: (index: number, handleType: 'in' | 'out', dx: number, dy: number, altKey: boolean) => void;
  onHandleDragMove: (index: number, handleType: 'in' | 'out', dx: number, dy: number, altKey: boolean) => void;
  onPointClick: (index: number) => void;
  onSegmentClick: (afterIndex: number) => void;
}

export class PathEditOverlay {
  private group: Konva.Group;
  private pathPreview: Konva.Path | null = null;
  private pointNodes: Konva.Circle[] = [];
  private handleNodes: Map<string, Konva.Circle> = new Map();
  private handleLines: Map<string, Konva.Line> = new Map();
  private segmentHitAreas: Konva.Line[] = [];
  private ghostPoint: Konva.Circle | null = null;
  private layer: Konva.Layer;
  private callbacks: PathEditCallbacks | null = null;
  private selectedPointIndex: number | null = null;

  private currentPoints: PathPoint[] = [];
  private currentClosed = false;
  private currentOffsetX = 0;
  private currentOffsetY = 0;
  private currentScaleX = 1;
  private currentScaleY = 1;

  constructor(layer: Konva.Layer) {
    this.layer = layer;
    this.group = new Konva.Group();
    this.layer.add(this.group);
  }

  setCallbacks(callbacks: PathEditCallbacks): void {
    this.callbacks = callbacks;
  }

  setSelectedPoint(index: number | null): void {
    this.selectedPointIndex = index;
    this.pointNodes.forEach((node, i) => {
      node.fill(i === index ? '#2563eb' : '#ffffff');
    });
    this.updateHandleVisibility();
    this.layer.batchDraw();
  }

  /** Convert local point coords to canvas position */
  private toCanvas(localX: number, localY: number): { x: number; y: number } {
    return {
      x: this.currentOffsetX + localX * this.currentScaleX,
      y: this.currentOffsetY + localY * this.currentScaleY,
    };
  }

  /** Convert canvas position back to local coords */
  private toLocal(canvasX: number, canvasY: number): { x: number; y: number } {
    return {
      x: (canvasX - this.currentOffsetX) / this.currentScaleX,
      y: (canvasY - this.currentOffsetY) / this.currentScaleY,
    };
  }

  show(
    points: PathPoint[],
    closed: boolean,
    offsetX: number,
    offsetY: number,
    scaleX = 1,
    scaleY = 1,
  ): void {
    this.clearAll();
    this.currentPoints = points.map((p) => ({ ...p }));
    this.currentClosed = closed;
    this.currentOffsetX = offsetX;
    this.currentOffsetY = offsetY;
    this.currentScaleX = scaleX;
    this.currentScaleY = scaleY;

    if (points.length === 0) return;

    // Path preview (dashed outline) — positioned and scaled to match the layer
    this.pathPreview = new Konva.Path({
      x: offsetX,
      y: offsetY,
      scaleX,
      scaleY,
      data: pathPointsToSVG(points, closed),
      stroke: '#2563eb',
      strokeWidth: 1.5 / Math.max(scaleX, scaleY), // keep visual stroke constant
      fill: '',
      dash: [4 / scaleX, 4 / scaleX],
      listening: false,
    });
    this.group.add(this.pathPreview);

    // Ghost point
    this.ghostPoint = new Konva.Circle({
      radius: 4,
      fill: 'rgba(37, 99, 235, 0.4)',
      stroke: '#2563eb',
      strokeWidth: 1,
      visible: false,
      listening: false,
    });
    this.group.add(this.ghostPoint);

    // Segment hit areas
    const segCount = closed ? points.length : points.length - 1;
    for (let i = 0; i < segCount; i++) {
      const a = this.toCanvas(points[i].x, points[i].y);
      const b = this.toCanvas(points[(i + 1) % points.length].x, points[(i + 1) % points.length].y);
      const segIdx = i;
      const seg = new Konva.Line({
        points: [a.x, a.y, b.x, b.y],
        stroke: 'transparent',
        strokeWidth: 14,
        hitStrokeWidth: 14,
      });
      seg.on('click', () => this.callbacks?.onSegmentClick(segIdx));
      seg.on('mouseenter', () => { if (this.ghostPoint) this.ghostPoint.visible(true); });
      seg.on('mouseleave', () => {
        if (this.ghostPoint) {
          this.ghostPoint.visible(false);
          this.layer.batchDraw();
        }
      });
      seg.on('mousemove', () => {
        if (!this.ghostPoint) return;
        const stage = this.layer.getStage();
        if (!stage) return;
        const pos = stage.getPointerPosition();
        if (!pos) return;
        const stageScale = stage.scaleX() || 1;
        const stageX = stage.x();
        const stageY = stage.y();
        this.ghostPoint.x((pos.x - stageX) / stageScale);
        this.ghostPoint.y((pos.y - stageY) / stageScale);
        this.ghostPoint.visible(true);
        this.layer.batchDraw();
      });
      this.segmentHitAreas.push(seg);
      this.group.add(seg);
    }

    // Points and handles
    for (let i = 0; i < points.length; i++) {
      const p = points[i];
      const cp = this.toCanvas(p.x, p.y);

      this.createHandle(i, 'in', cp.x, cp.y, p.handleIn);
      this.createHandle(i, 'out', cp.x, cp.y, p.handleOut);

      const pointIdx = i;
      const circle = new Konva.Circle({
        x: cp.x,
        y: cp.y,
        radius: 5,
        fill: i === this.selectedPointIndex ? '#2563eb' : '#ffffff',
        stroke: '#2563eb',
        strokeWidth: 2,
        draggable: true,
      });
      circle.on('click', () => this.callbacks?.onPointClick(pointIdx));
      circle.on('dragmove', () => {
        const local = this.toLocal(circle.x(), circle.y());
        this.updatePointPreview(pointIdx, local.x, local.y);
        this.callbacks?.onPointDragMove(pointIdx, local.x, local.y);
      });
      circle.on('dragend', () => {
        const local = this.toLocal(circle.x(), circle.y());
        this.callbacks?.onPointDragEnd(pointIdx, local.x, local.y);
      });
      this.pointNodes.push(circle);
      this.group.add(circle);
    }

    this.updateHandleVisibility();
    this.layer.batchDraw();
  }

  private createHandle(
    pointIndex: number,
    handleType: 'in' | 'out',
    canvasPx: number,
    canvasPy: number,
    handle: { x: number; y: number } | undefined,
  ): void {
    const key = `${pointIndex}-${handleType}`;
    // Handle offsets are in local space, scale them to canvas
    const hx = canvasPx + (handle?.x ?? 0) * this.currentScaleX;
    const hy = canvasPy + (handle?.y ?? 0) * this.currentScaleY;

    const line = new Konva.Line({
      points: [canvasPx, canvasPy, hx, hy],
      stroke: '#94a3b8',
      strokeWidth: 1,
      listening: false,
    });
    this.handleLines.set(key, line);
    this.group.add(line);

    const handleCircle = new Konva.Circle({
      x: hx,
      y: hy,
      radius: 3.5,
      fill: '#2563eb',
      stroke: '#ffffff',
      strokeWidth: 1,
      draggable: true,
    });
    handleCircle.on('dragmove', (e) => {
      // Convert handle delta back to local space
      const dx = (handleCircle.x() - canvasPx) / this.currentScaleX;
      const dy = (handleCircle.y() - canvasPy) / this.currentScaleY;
      const altKey = (e.evt as MouseEvent).altKey;
      line.points([canvasPx, canvasPy, handleCircle.x(), handleCircle.y()]);
      this.updateHandlePreview(pointIndex, handleType, dx, dy, altKey);
      this.callbacks?.onHandleDragMove(pointIndex, handleType, dx, dy, altKey);
    });
    handleCircle.on('dragend', (e) => {
      const dx = (handleCircle.x() - canvasPx) / this.currentScaleX;
      const dy = (handleCircle.y() - canvasPy) / this.currentScaleY;
      const altKey = (e.evt as MouseEvent).altKey;
      this.callbacks?.onHandleDragEnd(pointIndex, handleType, dx, dy, altKey);
    });
    this.handleNodes.set(key, handleCircle);
    this.group.add(handleCircle);
  }

  private updateHandleVisibility(): void {
    for (let i = 0; i < this.currentPoints.length; i++) {
      const p = this.currentPoints[i];
      const isSelected = i === this.selectedPointIndex;

      for (const type of ['in', 'out'] as const) {
        const key = `${i}-${type}`;
        const handleNode = this.handleNodes.get(key);
        const lineNode = this.handleLines.get(key);
        if (!handleNode || !lineNode) continue;

        const handle = type === 'in' ? p.handleIn : p.handleOut;
        const hasHandle = handle && (Math.abs(handle.x) > 0.1 || Math.abs(handle.y) > 0.1);
        const visible = isSelected || !!hasHandle;
        handleNode.visible(visible);
        lineNode.visible(visible);
      }
    }
  }

  private refreshPathPreview(): void {
    if (this.pathPreview) {
      this.pathPreview.data(pathPointsToSVG(this.currentPoints, this.currentClosed));
    }
    this.layer.batchDraw();
  }

  private updatePointPreview(index: number, newX: number, newY: number): void {
    this.currentPoints[index] = { ...this.currentPoints[index], x: newX, y: newY };

    const cp = this.toCanvas(newX, newY);
    const p = this.currentPoints[index];
    for (const type of ['in', 'out'] as const) {
      const key = `${index}-${type}`;
      const handleNode = this.handleNodes.get(key);
      const lineNode = this.handleLines.get(key);
      if (!handleNode || !lineNode) continue;
      const handle = type === 'in' ? p.handleIn : p.handleOut;
      const hx = cp.x + (handle?.x ?? 0) * this.currentScaleX;
      const hy = cp.y + (handle?.y ?? 0) * this.currentScaleY;
      handleNode.x(hx);
      handleNode.y(hy);
      lineNode.points([cp.x, cp.y, hx, hy]);
    }

    this.refreshPathPreview();
  }

  private updateHandlePreview(
    index: number,
    handleType: 'in' | 'out',
    dx: number,
    dy: number,
    altKey: boolean,
  ): void {
    const p = { ...this.currentPoints[index] };
    if (handleType === 'out') {
      p.handleOut = { x: dx, y: dy };
      if (!altKey) p.handleIn = { x: -dx, y: -dy };
    } else {
      p.handleIn = { x: dx, y: dy };
      if (!altKey) p.handleOut = { x: -dx, y: -dy };
    }
    this.currentPoints[index] = p;

    // Update opposite handle visual if mirroring
    if (!altKey) {
      const oppositeType = handleType === 'in' ? 'out' : 'in';
      const oppositeKey = `${index}-${oppositeType}`;
      const oppositeNode = this.handleNodes.get(oppositeKey);
      const oppositeLine = this.handleLines.get(oppositeKey);
      if (oppositeNode && oppositeLine) {
        const cp = this.toCanvas(p.x, p.y);
        const oHandle = oppositeType === 'in' ? p.handleIn : p.handleOut;
        const ox = cp.x + (oHandle?.x ?? 0) * this.currentScaleX;
        const oy = cp.y + (oHandle?.y ?? 0) * this.currentScaleY;
        oppositeNode.x(ox);
        oppositeNode.y(oy);
        oppositeLine.points([cp.x, cp.y, ox, oy]);
        oppositeNode.visible(true);
        oppositeLine.visible(true);
      }
    }

    this.refreshPathPreview();
  }

  hide(): void {
    this.clearAll();
    this.layer.batchDraw();
  }

  private clearAll(): void {
    this.group.destroyChildren();
    this.pathPreview = null;
    this.ghostPoint = null;
    this.pointNodes = [];
    this.handleNodes.clear();
    this.handleLines.clear();
    this.segmentHitAreas = [];
    this.currentPoints = [];
  }

  destroy(): void {
    this.group.destroy();
  }
}
